import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useAppDispatch, useAppSelector } from 'store'

import { fetchBatchTranslationProgress } from 'services/productsSlice'
import {
  selectTranslationProgress,
  updateTranslationStatus
} from 'services/translationsSlice'
import { IBatchProgress } from 'types/products'
import { TRANSLATION_STATUS } from 'types/translations'
import { mapTranslationStatus } from 'utils/translationUtils'

interface UseBatchProgressPollingProps {
  batchId?: string
  enabled?: boolean
  interval?: number
  maxPollTime?: number
}

const IN_PROGRESS_STATUSES = [
  TRANSLATION_STATUS.pending,
  TRANSLATION_STATUS.validating,
  TRANSLATION_STATUS.translating,
  TRANSLATION_STATUS.copying_files,
  TRANSLATION_STATUS.creating_product,
  TRANSLATION_STATUS.translating_assets,
  TRANSLATION_STATUS.finalizing
]

export const useBatchProgressPolling = ({
  batchId,
  enabled = true,
  interval = 3500,
  maxPollTime = 300000
}: UseBatchProgressPollingProps) => {
  const dispatch = useAppDispatch()
  const translationProgress = useAppSelector(state =>
    selectTranslationProgress(state, batchId)
  )
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const startTimeRef = useRef<number>(Date.now())

  const isInProgress =
    translationProgress &&
    IN_PROGRESS_STATUSES.includes(translationProgress.status)
  const isCompleted =
    translationProgress?.status === TRANSLATION_STATUS.completed
  const isFailed = translationProgress?.status === TRANSLATION_STATUS.failed

  const shouldPoll = useMemo(() => {
    if (!enabled || !batchId) return false
    if (!translationProgress) return true
    if (isCompleted || isFailed) return false

    return isInProgress
  }, [
    enabled,
    batchId,
    translationProgress,
    isCompleted,
    isFailed,
    isInProgress
  ])

  const pollProgress = useCallback(async () => {
    if (!batchId) return

    try {
      const action = await dispatch(fetchBatchTranslationProgress(batchId))

      if (action.meta.requestStatus !== 'fulfilled') return

      const response = action.payload as any
      const batchProgress: IBatchProgress | undefined =
        response?.data ?? response

      if (!batchProgress?.id) return

      const totalProducts =
        batchProgress.totalProducts || batchProgress.productIds?.length || 1
      const completedProducts =
        batchProgress.completedProducts || batchProgress.successfulProducts || 0
      const isCompleted =
        batchProgress.status === 'completed' ||
        batchProgress.status === 'partial_success'

      const translationProgress = {
        id: batchProgress.id,
        productId: `batch_${totalProducts}_products`,
        sourceCountry: batchProgress.sourceCountry,
        targetCountry: batchProgress.targetCountry,
        status: mapTranslationStatus(batchProgress.status),
        progress: isCompleted ? 100 : batchProgress.progress || 0,
        message: batchProgress.message || 'Processing batch...',
        startTime: batchProgress.startTime,
        endTime: batchProgress.endTime,
        error: batchProgress.error,
        totalAssets: totalProducts,
        translatedAssetCount: isCompleted ? totalProducts : completedProducts,
        estimatedTimeRemaining: batchProgress.estimatedTimeRemaining
      }

      await dispatch(updateTranslationStatus(translationProgress))
    } catch (error) {
      console.error('Failed to fetch batch translation progress:', error)
    }
  }, [batchId, dispatch])

  useEffect(() => {
    try {
      if (shouldPoll) {
        startTimeRef.current = Date.now()
        // Poll immediately when starting
        pollProgress()

        intervalRef.current = setInterval(() => {
          try {
            if (Date.now() - startTimeRef.current > maxPollTime) {
              console.warn('Batch translation polling timeout reached')
              if (intervalRef.current) {
                clearInterval(intervalRef.current)
                intervalRef.current = null
              }

              return
            }

            pollProgress()
          } catch (error) {
            console.error('Error in batch polling interval:', error)
          }
        }, interval)
      } else {
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
          intervalRef.current = null
        }
      }
    } catch (error) {
      console.error('Error in useBatchProgressPolling useEffect:', error)
    }

    return () => {
      try {
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
          intervalRef.current = null
        }
      } catch (error) {
        console.error('Error cleaning up batch polling interval:', error)
      }
    }
  }, [shouldPoll, batchId, interval, maxPollTime, pollProgress])

  return {
    batchProgress: translationProgress,
    isInProgress,
    isCompleted,
    isFailed
  }
}
